import type { ComponentProps, ReactNode } from "react";
import { useState } from "react";
import { tv } from "tailwind-variants";

import { IconWrapper } from "../common";
import { Label } from "./label";
import { Message } from "./message";

const textAreaVariants = tv({
  slots: {
    fieldContainer: "relative",
    textAreaContainer: "relative flex flex-row items-start",
    textArea: [
      "block w-full px-3 py-2 text-sm text-content",
      "placeholder:text-content-subtle",
      "transition-[color,box-shadow,border-color] duration-150",
      "focus:outline-none focus-visible:outline-none",
      "disabled:cursor-not-allowed disabled:text-content-subtle",
    ],
    counter: "py-1 text-xs text-content-muted tabular-nums",
  },
  variants: {
    /*
      The field's own border, background and shadow. A composer that draws one
      surface around the textarea and its send button turns this off: overriding
      it from the outside does not work, because tailwind-merge cannot collapse
      the custom `shadow-raised` against `shadow-none` and both survive, leaving
      a shadow across the middle of the composer.
    */
    chrome: {
      true: {
        textArea: [
          "rounded-lg border border-line-control bg-surface shadow-raised",
          "hover:border-content-subtle",
          "focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-focus-ring/40",
          "disabled:border-line-strong disabled:bg-surface-sunken",
        ],
      },
    },
    error: {
      true: {
        textArea: "border-danger focus-visible:border-danger focus-visible:ring-danger/30",
      },
    },
    left: {
      true: { textArea: "pl-10" },
    },
    atLimit: {
      true: { counter: "font-medium text-warning-content" },
    },
  },
  defaultVariants: {
    chrome: true,
  },
});

const { counter, fieldContainer, textArea, textAreaContainer } = textAreaVariants();

export type TextAreaProps = {
  chrome?: boolean;
  compact?: boolean;
  containerClassName?: string;
  iconClassName?: string;
  labelClassName?: string;
  error?: string | boolean;
  id: string;
  label?: ReactNode;
  left?: ReactNode;
  message?: string;
  maxLength?: number;
  name: string;
} & ComponentProps<"textarea">;

export const TextArea = ({
  chrome = true,
  className,
  compact = true,
  containerClassName,
  error,
  id,
  label,
  labelClassName,
  left,
  maxLength,
  message,
  name,
  onChange,
  ...rest
}: TextAreaProps) => {
  const [valueLength, setValueLength] = useState(0);

  const hasError = Boolean(error);
  const messageId = `${id}-message`;
  const counterId = `${id}-counter`;
  const showMessage = !compact || Boolean(message) || hasError;

  const describedBy =
    [showMessage ? messageId : null, maxLength ? counterId : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className={fieldContainer({ className: containerClassName })}>
      {label ? (
        <Label
          className={labelClassName}
          disabled={rest.disabled}
          htmlFor={id}
          label={label}
          required={rest.required}
        />
      ) : null}

      <div className={textAreaContainer()}>
        {left ? (
          <div className="pointer-events-none absolute top-2 left-0 pl-3 text-content-subtle">
            <IconWrapper size="sm">{left}</IconWrapper>
          </div>
        ) : null}

        <textarea
          id={id}
          name={name}
          {...rest}
          aria-describedby={describedBy ?? rest["aria-describedby"]}
          aria-invalid={hasError || undefined}
          className={textArea({ chrome, className, error: hasError, left: Boolean(left) })}
          onChange={(e) => {
            if (maxLength && e.target.value.length > maxLength) {
              e.target.value = e.target.value.slice(0, maxLength);
            }
            setValueLength(e.target.value.length);
            onChange?.(e);
          }}
        />
      </div>

      <div className="flex flex-row items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Message error={error} id={messageId} message={message} reserveSpace={!compact} />
        </div>

        {maxLength ? (
          <p className={counter({ atLimit: valueLength >= maxLength })} id={counterId}>
            <span className="sr-only">Characters used: </span>
            {valueLength} / {maxLength}
          </p>
        ) : null}
      </div>
    </div>
  );
};
